import { c as _c } from "react/compiler-runtime";
import type { CollectedDataOption, DataIdentifier } from '@onefootprint/request-types/dashboard';
import { CdoToAllDisMap, CollectedKybDataOption, type DataIdentifier as LegacyDataIdentifier } from '@onefootprint/types';
import { useTranslation } from 'react-i18next';
type TranslatedField = {
  key: string;
  /** Translated display name. We will only show one entry for each display name */
  displayName: string;
  /** A key used to sort fields, which allows grouping fields under the same CDO close to each other */
  sortKey: string;
};

/** Groups fields into a deterministic order, sorted by the parent CDO. This places fields under the same CDO close to each other. */
const useTranslateAndSortFields = (dis, cdos) => {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "di"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t: diT
  } = useTranslation("common", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "cdo"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t: cdoT
  } = useTranslation("common", t1);
  let t2;
  if ($[2] !== diT) {
    t2 = di => {
      if (di.startsWith("custom.")) {
        return {
          key: di,
          displayName: di,
          sortKey: "custom"
        };
      }
      if (di.startsWith("business.beneficial_owners.") || di === "business.beneficial_owner_explanation_message") {
        return {
          key: di,
          displayName: diT(_temp),
          sortKey: CollectedKybDataOption.kycedBeneficialOwners
        };
      }
      const cdoEntry = Object.entries(CdoToAllDisMap).find(t3 => {
        const [, dis_0] = t3;
        return dis_0.includes(di as LegacyDataIdentifier);
      });
      if (!cdoEntry) {
        const translated = diT($_0 => $_0[di]);
        const isMissingTranslation = translated === di || translated.startsWith("di.");
        return {
          key: di,
          displayName: isMissingTranslation ? di : translated,
          sortKey: di.split(".").slice(1).join(".")
        };
      }
      const [cdo, dis_1] = cdoEntry;
      const idxInCdo = dis_1.indexOf(di as LegacyDataIdentifier);
      const translated_0 = diT($_1 => $_1[di]);
      const isMissingTranslation_0 = translated_0 === di || translated_0.startsWith("di.");
      return {
        key: di,
        displayName: isMissingTranslation_0 ? di : translated_0,
        sortKey: `${cdo}.${idxInCdo}`
      };
    };
    $[2] = diT;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const translateDi = t2;
  let t3;
  if ($[4] !== cdoT) {
    t3 = cdo_0 => ({
      key: cdo_0,
      displayName: cdoT($_2 => $_2[cdo_0 as keyof typeof $_2]),
      sortKey: cdo_0
    });
    $[4] = cdoT;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const translateCdo = t3;
  let t4;
  if ($[6] !== cdos || $[7] !== dis || $[8] !== translateCdo || $[9] !== translateDi) {
    const allFields = [...dis.map(translateDi), ...cdos.map(translateCdo)];
    const distinctOnDisplayName = _temp2;
    const uniqueFields = allFields.filter(distinctOnDisplayName);
    const sortedFields = uniqueFields.sort(_temp3);
    t4 = sortedFields.map(_temp4);
    $[6] = cdos;
    $[7] = dis;
    $[8] = translateCdo;
    $[9] = translateDi;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const sortedDisplayNames = t4;
  return sortedDisplayNames;
};
export default useTranslateAndSortFields;
function _temp($) {
  return $.business.beneficial_owners;
}
function _temp2(field, index, self) {
  const firstIndex = self.findIndex(t => t.displayName === field.displayName);
  if (index === firstIndex) {
    return true;
  }
  if (field.displayName === field.key) {
    return true;
  }
  return false;
}
function _temp3(a, b) {
  const sortKey = a.sortKey.localeCompare(b.sortKey);
  if (sortKey !== 0) {
    return sortKey;
  }
  return a.key.localeCompare(b.key);
}
function _temp4(field_0) {
  return field_0.displayName;
}